import * as THREE from 'three';
import { TweenMax, Expo } from 'gsap/TweenMax';
import isMobile from '@/scripts/utils/detect-mobile';
import _debounce from 'lodash/debounce';
import isIe from '@/scripts/utils/detect-ie';
import Controller from '@/scripts/utils/scrollmagic/controller';
import ScrollMagic from 'scrollmagic';

import vertex from './vertex.glsl';
import fragment from './fragment.glsl';
import { getUvRate, createButton, resolveTexture } from './helper';

const defaults = {
  element: null,
  intensity: 1,
  speedIn: 1.6,
  speedOut: 1.2,
  userHover: true,
  easing: Expo.easeOut,
};

export default class HoverDistortion {
  constructor(props) {
    const options = Object.assign({}, defaults, props);
    this.element = options.element;
    if (!this.element) return;
    this.element.classList.add('custom-image');
    this.displacemenetImage = this.element.dataset.displacementImage;
    this.imageMain = JSON.parse(this.element.dataset.imageMain);
    this.imageSub = JSON.parse(this.element.dataset.imageSub);
    this.hasButton = this.element.dataset.hasButton;
    this.intensity = options.intensity;
    this.speedIn = options.speedIn;
    this.speedOut = options.speedOut;
    this.easing = options.easing;
    this.userHover = options.userHover;

    this.dpi = window.devicePixelRatio;
    this.vw = $(this.element).width();
    this.vh = $(this.element).height();

    // THEE init
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(
      70,
      this.vw / this.vh,
      0.001, 100,
    );
    this.camera.position.z = 1;

    this.renderer = new THREE.WebGLRenderer();

    this.loader = new THREE.TextureLoader();
    this.loader.crossOrigin = '';

    this.material = null;
    this.plane = null;
    this.video = null;
    this.bindedRender = this.render.bind(this);

    this.init();
  }

  next() {
    this.playVideo();
    TweenMax.ticker.addEventListener('tick', this.bindedRender);
    TweenMax.to(this.material.uniforms.dispFactor, this.speedIn, {
      value: 1,
      ease: this.easing,
    });
  }

  previous() {
    TweenMax.to(this.material.uniforms.dispFactor, this.speedOut, {
      value: 0,
      ease: this.easing,
      onComplete: () => {
        TweenMax.ticker.removeEventListener('tick', this.bindedRender);
        this.stopVideo();
      },
    });
  }

  initHoverEvents() {
    let evtIn = 'mouseenter';
    let evtOut = 'mouseleave';
    if (isMobile()) {
      evtIn = 'touchstart';
      evtOut = 'touchend';
    }

    this.element.addEventListener(evtIn, this.next.bind(this));

    this.element.addEventListener(evtOut, this.previous.bind(this));
  }

  playVideo() {
    return this.video && this.video.play();
  }

  stopVideo() {
    if (this.video) {
      this.video.pause();
      this.video.currentTime = 0;
    }
  }

  loadVideo() {
    if (this.video.readyState !== this.video.HAVE_ENOUGH_DATA) {
      this.video.load();
    }
  }

  prepareTexture(object) {
    return new Promise((resolve) => {
      const { type, src } = object;
      if (type === 'image') {
        this.loader.load(src, (texture) => {
          resolveTexture({ resolve, texture });
        });
      } else {
        let texture;
        this.video = document.createElement('video');
        this.video.addEventListener('loadedmetadata', () => {
          texture.needsUpdate = true;
          resolveTexture({ resolve, texture });
        });
        this.video.setAttribute('playsinline', true);
        this.video.setAttribute('webkit-playsinline', true);
        this.video.preload = 'metadata';
        this.video.src = src;
        this.video.muted = true;
        this.video.loop = true;

        if (isIe()) {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d', { alpha: false });
          this.video.addEventListener('loadedmetadata', () => {
            canvas.width = this.video.videoWidth;
            canvas.height = this.video.videoHeight;
          });
          texture = new THREE.Texture(canvas);
          TweenMax.ticker.addEventListener('tick', () => {
            if (this.video.readyState === this.video.HAVE_ENOUGH_DATA) {
              ctx.drawImage(this.video, 0, 0);
              texture.needsUpdate = true;
            }
          });
        } else {
          texture = new THREE.VideoTexture(this.video);
        }
        this.lazyLoadVideo();
      }
    });
  }

  lazyLoadVideo() {
    return new ScrollMagic.Scene({
      triggerElement: this.element,
      triggerHook: 'onEnter',
      offset: -200,
    })
      .on('start', () => {
        this.loadVideo();
      })
      // .addIndicators({ name: 'Hover Distortion video loading' })
      .addTo(Controller);
  }

  resize() {
    if (!this.renderer) return;
    this.vw = $(this.element).width();
    this.vh = $(this.element).height();
    this.renderer.setSize(this.vw, this.vh);
    this.camera.aspect = this.vw / this.vh;
    this.material.uniforms.pixels.value.x = this.vw;
    this.material.uniforms.pixels.value.y = this.vh;

    // // calculate scene
    const dist = this.camera.position.z - this.plane.position.z;
    const height = 1;
    this.camera.fov = 2 * (180 / Math.PI) * Math.atan(height / (2 * dist));
    //
    if (this.vw / this.vh > 1) {
      this.plane.scale.x = this.vw / this.vh;
      this.plane.scale.y = this.vw / this.vh;
    }


    this.camera.updateProjectionMatrix();
    this.render();
  }

  render() {
    if (!this.renderer) return;
    this.renderer.render(this.scene, this.camera);
  }

  async init() {
    this.renderer.setPixelRatio(this.dpi);
    this.renderer.setSize(this.vw, this.vh);
    this.element.appendChild(this.renderer.domElement);

    if (this.hasButton) {
      $(this.element).append(createButton(this.element.dataset.buttonParams));
    }
    this.element.addEventListener('destroy', () => {
      this.renderer.forceContextLoss();
      this.renderer.context = null;
      this.renderer.domElement.remove();
      this.renderer.domElement = null;
      this.renderer = null;
      this.scene = null;
    });

    const [texture1, texture2] = [await this.prepareTexture(this.imageMain), await this.prepareTexture(this.imageSub)];
    if (texture1 && texture2) {
      const disp = this.loader.load(this.displacemenetImage);
      disp.wrapS = disp.wrapT = THREE.RepeatWrapping; // eslint-disable-line no-multi-assign

      this.material = new THREE.ShaderMaterial({
        uniforms: {
          effectFactor: { type: 'f', value: this.intensity },
          dispFactor: { type: 'f', value: 0.0 },
          texture: { type: 't', value: texture1 },
          texture2: { type: 't', value: texture2 },
          disp: { type: 't', value: disp },
          pixels: { type: 'v2', value: new THREE.Vector2(this.vw, this.vh) },
          uvRate1: {
            value: getUvRate(texture1.image.width, texture1.image.height),
          },
          uvRate2: {
            value: $(texture2.image).is('video')
              ? getUvRate(texture2.image.videoWidth, texture2.image.videoHeight)
              : getUvRate(texture2.image.width, texture2.image.height),
          },
        },

        vertexShader: vertex,
        fragmentShader: fragment,
        transparent: true,
        opacity: 1.0,
      });

      const geometry = new THREE.PlaneGeometry(1, 1, 1, 1);
      this.plane = new THREE.Mesh(geometry, this.material);

      this.scene.add(this.plane);

      if (this.userHover) {
        this.initHoverEvents();
      }

      if (!this.video) {
        this.element.classList.add('img-container_zoom-in');
      }

      requestAnimationFrame(() => {
        this.resize();
        window.addEventListener('resize', _debounce(() => this.resize(), 100));
        // element can be parallaxed, 'resize' comes from parallax-img function
        this.element.addEventListener('resize', _debounce(() => this.resize(), 100));
      });
    }
  }
}



// WEBPACK FOOTER //
// ./scripts/elements/HoverDistortion/HoverDistortion.js